import { Input } from '@/components/@core/input';
import { MenuItem, Select, Typography, useTheme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';

type ExtraFields = {
  name: string;
  title: string;
  required: true;
  field_type: 'SELECT' | 'CHARFIELD';
  choices: [
    {
      id: string;
      name: string;
    },
    {
      id: string;
      name: string;
    },
  ];
};

export const getExtraFieldsObject = (
  extraFields: ExtraFields[],
  extraFieldValues: {
    [key: string]: unknown;
  },
) => {
  return extraFields.reduce((obj, item) => {
    // @ts-ignore
    obj[item.name] = extraFieldValues[item.name] ?? '';
    return obj;
  }, {} as ExtraFields);
};

export const ExtraFieldsInputs = ({
  extra_fields,
  getFieldProps,
}: {
  extra_fields: ExtraFields[];
  getFieldProps: any;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const labelSx = {
    fontFamily: 'Inter, sans-serif',
    fontSize: theme.fontSizes.caption,
    color: theme.palette.text.secondary,
    display: 'block',
    mb: 0.5,
  };

  return (
    <>
      {extra_fields?.map((field) => {
        if (field.field_type === 'SELECT') {
          return (
            <Fragment key={field.name}>
              <Typography component="label" sx={labelSx}>
                {field.title}
              </Typography>
              <Select
                {...getFieldProps(`extra_fields.${field.name}`)}
                displayEmpty
                size="small"
                fullWidth
                inputProps={{ 'aria-label': 'Without label' }}
                IconComponent={() => (
                  <ChevronDown
                    size={18}
                    color={theme.palette.text.secondary}
                    style={{ marginRight: '8px', flexShrink: 0 }}
                  />
                )}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.body,
                  backgroundColor: 'white',
                  borderRadius: '14px',
                  color: theme.palette.text.primary,
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                    borderRadius: '14px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${theme.palette.primary.main}60`,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '1.5px',
                  },
                }}
              >
                <MenuItem
                  disabled
                  value=""
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: theme.fontSizes.body,
                  }}
                >
                  {t('general.selectOption')}
                </MenuItem>
                {field.choices?.map((choice) => (
                  <MenuItem
                    key={choice.id}
                    value={choice.id}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: theme.fontSizes.body,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {choice.name}
                  </MenuItem>
                ))}
              </Select>
            </Fragment>
          );
        }
        return (
          <Input
            label={field.title}
            key={field.name}
            style={{ marginBottom: '1rem' }}
            inputProps={{
              type: 'text',
              ...getFieldProps('extra_fields.' + field.name),
            }}
          />
        );
      })}
    </>
  );
};
